import { Trans, plural, t } from "@web-monorepo/i18n/macro";
import {
  DDSButton,
  DDSDialog,
  DDSDialogBody,
  DDSDialogContent,
  DDSDialogFooter,
  DDSDialogHeader,
  DDSTextField,
} from "@web-monorepo/dds";
import React from "react";
import type { ToolkitAction } from "src/state/toolkitReduxStore";
import { appMetadataTimer, RECEIVE_TIME, Timer } from "src/pods/timer";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import logEvent from "src/utils/logEvent";
import TimerDisplay from "./TimerDisplay";
import styles from "./TimerRemote.module.css";

const TIMES: Timer[] = [{ seconds: 15 }, { seconds: 30 }, { seconds: 60 }, { seconds: 300 }, { seconds: 600 }];

const initialState = {
  selectedTime: null,
};

type TimerRemoteProps = {
  classId: string;
  mountPoint: string;
  displayOpen: (url: string, action?: ToolkitAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  sendTime: (time: Timer) => void;
  time: Timer;
  isCasting: boolean;
  isConnected: boolean;
};

type TimerRemoteState = {
  minutesInput?: number | null;
  secondsInput?: number | null;
  selectedTime: Timer | null;
  askForTime?: boolean;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class TimerRemote extends React.Component<TimerRemoteProps, TimerRemoteState> {
  constructor(props: TimerRemoteProps) {
    super(props);
    this.state = initialState;
  }

  componentDidMount() {
    logEvent({
      eventName: "timer.setup.open",
    });
  }

  componentWillUnmount() {
    logEvent({
      eventName: "timer.setup.close",
    });
  }

  render() {
    const { classId, mountPoint, isCasting, isConnected } = this.props;

    function renderTime(this: TimerRemote, time: Timer) {
      const totalSeconds = time.seconds || 0;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = String(totalSeconds % 60).padStart(2, "0");
      return (
        <li key={time.seconds} className={styles.timesListItem}>
          <DDSButton
            variant="unstyled"
            data-name="timer_remote:time_remaining"
            className={styles.timeButton}
            onClick={() => this.openDisplay(time)}
            aria-label={
              minutes && totalSeconds % 60 === 0
                ? plural(minutes, { one: "# minute", other: "# minutes" })
                : plural(totalSeconds, { one: "# second", other: "# seconds" })
            }
          >
            {`${minutes}:${seconds}`}
          </DDSButton>
        </li>
      );
    }

    return (
      <AppPage
        {...appMetadataTimer()}
        classId={classId}
        mountPoint={mountPoint}
        disableFocusTrap={this.state.askForTime}
      >
        <div className={styles.container}>
          <section className={styles.times}>
            <ul className={styles.timesList}>
              {TIMES.map(renderTime, this)}
              <li className={styles.timesListItem}>
                <DDSButton
                  variant="unstyled"
                  data-name="timer_remote:select_custom_time"
                  className={`${styles.timeButton} ${styles.addTimeButton}`}
                  onClick={this.openTimeInput}
                  aria-label={t`Custom timer`}
                >
                  +
                </DDSButton>
              </li>
            </ul>
          </section>
          {this.state.askForTime && (
            <DDSDialog data-name="timer_remote:custom_time_dialog" open>
              <DDSDialogContent data-name="timer_remote:custom_time_dialog:content" onClose={this.closeTimeInput}>
                <DDSDialogHeader visuallyHideTitle title={t`Set custom time`} onClose={this.closeTimeInput} />
                <DDSDialogBody>
                  <div className={styles.timeInputRow}>
                    <div className={styles.textField}>
                      <DDSTextField
                        data-name="timer_remote:minutes"
                        label={t`Minutes`}
                        type="number"
                        onChange={this.onMinutesChange}
                      />
                    </div>
                    <span className={styles.inputSeparator} aria-hidden="true">
                      :
                    </span>
                    <div className={styles.textField}>
                      <DDSTextField
                        data-name="timer_remote:seconds"
                        label={t`Seconds`}
                        type="number"
                        onChange={this.onSecondsChange}
                      />
                    </div>
                  </div>
                </DDSDialogBody>
                <DDSDialogFooter>
                  <DDSButton variant="tertiary" data-name="timer_remote:cancel" onClick={this.closeTimeInput}>
                    <Trans>Cancel</Trans>
                  </DDSButton>
                  <DDSButton
                    data-name="timer_remote:start"
                    onClick={() => {
                      const { minutesInput, secondsInput } = this.state;
                      this.closeTimeInput();
                      this.openDisplay({
                        seconds: (minutesInput || 0) * 60 + (secondsInput || 0),
                      });
                    }}
                  >
                    <Trans>Start</Trans>
                  </DDSButton>
                </DDSDialogFooter>
              </DDSDialogContent>
            </DDSDialog>
          )}
          {this.state.selectedTime && (
            <AppModal
              {...appMetadataTimer()}
              onRequestHide={() => {
                this.props.displayClose();
                this.props.remoteClose();
              }}
              onRequestStart={() => this.props.displayOpen("/apps/display/timer")}
              onRequestStop={() => this.props.displayClose()}
            >
              <TimerDisplay
                shouldRingBell={!(isCasting && isConnected)}
                time={this.props.time}
                sendTime={this.sendTime}
                fullscreen={false}
              />
            </AppModal>
          )}
        </div>
      </AppPage>
    );
  }

  sendTime = (time: Timer) => {
    const payload = {
      ...time,
      startTime: Date.now(),
    };
    this.props.sendTime(payload);
    this.props.displayOpen("/apps/display/timer", { type: RECEIVE_TIME, payload });
  };

  openTimeInput = () => {
    this.setState({ askForTime: true, minutesInput: null, secondsInput: null });
  };

  closeTimeInput = () => {
    this.setState({ askForTime: false });
  };

  onSecondsChange = (value: string) => {
    this.setState({ secondsInput: parseInt(value, 10) || null });
  };

  onMinutesChange = (value: string) => {
    this.setState({ minutesInput: parseInt(value, 10) || null });
  };

  openDisplay = (time: Timer) => {
    this.sendTime(time);

    if (this.props.mountPoint === "mobile") this.setState({ selectedTime: time });
    logEvent({
      eventName: TIMES.includes(time) ? "timer.start" : "timer.start.custom",
      eventValue: String(time.seconds),
    });
  };

  closeDisplay = () => {
    this.setState({ selectedTime: null });
    this.props.displayClose();
  };
}
